// Overview — "is everything OK right now?"
//
// Three live sections: what's deploying, what needs attention, and the most
// recent activity. Summary stats link into the deeper pages.

import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  CheckCircleOutline as HealthyIcon,
  ErrorOutline as FailIcon,
  Sync as DeployIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { groupByApp, isActiveDeploy, isRollbackProblem } from '../lib/grouping';
import { deploymentKey } from '../lib/records';
import { getColors, getElevation, rounded } from '../theme/tokens';
import { reduceMotionSx, transition } from '../theme/motion';
import { useCountUp } from '../hooks/useCountUp';
import { DEFAULT_CONFIG } from '../../utils/needsAttentionFilter';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import PageHeader from '../components/PageHeader';
import DeploymentRow from '../components/DeploymentRow';
import EmptyState from '../components/EmptyState';
import InfoPopover from '../components/InfoPopover';

const SectionCard: React.FC<{
  title: string;
  count?: number;
  hue?: string;
  action?: React.ReactNode;
  info?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, count, hue, action, info, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden', mb: 3 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${c.hairline}`,
          backgroundColor: c.canvasSoft,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            color: hue || c.inkMuted,
          }}
        >
          {title}
        </Typography>
        {count !== undefined && (
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: hue || c.inkMuted }}>
            {count}
          </Typography>
        )}
        {info}
        <Box sx={{ flexGrow: 1 }} />
        {action}
      </Box>
      {children}
    </Paper>
  );
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  hue?: string;
  to: string;
  icon?: React.ReactNode;
}> = ({ label, value, hue, to, icon }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  // Animate numeric values from their previous count; pass anything else through.
  const isNumber = typeof value === 'number';
  const counted = useCountUp(isNumber ? (value as number) : 0);
  return (
    <Paper
      component={RouterLink}
      to={to}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
        textDecoration: 'none',
        display: 'block',
        transition: transition(['border-color', 'box-shadow', 'transform']),
        '&:hover': {
          borderColor: c.inkFaint,
          transform: 'translateY(-2px)',
          boxShadow: getElevation(theme.palette.mode).soft,
        },
        ...reduceMotionSx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {icon}
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            color: c.inkMuted,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.5px', color: hue || c.ink }}>
        {isNumber ? counted : value}
      </Typography>
    </Paper>
  );
};

const OverviewPage: React.FC = () => {
  const { staticRecords, currentRecords, loading, loadingProgress, uniqueEnvs } = useDeploymentData();

  const deployingRecords = useMemo(
    () =>
      currentRecords
        .filter(r => isActiveDeploy(r))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [currentRecords]
  );

  // A service only needs attention while its latest version is failing — once a
  // newer deploy succeeds (or the failure ages out off-prod), it drops off.
  const appGroups = useMemo(
    () => groupByApp(currentRecords, DEFAULT_CONFIG),
    [currentRecords]
  );

  const failingApps = useMemo(() => appGroups.filter(g => g.status === 'failures'), [appGroups]);
  const healthyCount = useMemo(() => appGroups.filter(g => g.status === 'healthy').length, [appGroups]);

  // For each failing app surface its concrete failed records (latest first).
  const failingRecords = useMemo(
    () =>
      failingApps
        .flatMap(g => g.records.filter(r => r.status?.toLowerCase() === 'failed'))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [failingApps]
  );
  // A rollback (or failed rollback) needs attention regardless of the app's
  // overall rollup — surface these directly from the live records, not via the
  // failing-apps path, so a rolled-back service in an otherwise-busy app still
  // shows up. (DEVOPS-1629)
  const rollbackProblemRecords = useMemo(
    () => currentRecords.filter(isRollbackProblem),
    [currentRecords]
  );

  // Needs-attention = status failures ∪ rollback problems, deduped by id, newest first.
  const attentionRecords = useMemo(() => {
    const byId = new Map<string, DeploymentRecord>();
    [...failingRecords, ...rollbackProblemRecords].forEach(r => {
      const key = r.id || deploymentKey(r);
      if (!byId.has(key)) byId.set(key, r);
    });
    return Array.from(byId.values()).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [failingRecords, rollbackProblemRecords]);

  const MAX_FAILING_SHOWN = 25;
  const shownAttentionRecords = attentionRecords.slice(0, MAX_FAILING_SHOWN);

  const recentRecords = useMemo(
    () =>
      [...staticRecords]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 15),
    [staticRecords]
  );

  if (loading && staticRecords.length === 0) {
    return (
      <Box>
        <PageHeader title="Overview" />
        {loadingProgress && (
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.6 }}>
            Loading deployments… chunk {loadingProgress.current} of {loadingProgress.total}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" animation="wave" height={88} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" animation="wave" height={220} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rounded" animation="wave" height={320} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Overview" subtitle="Live state across all apps and environments" />

      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <StatCard
          label="Deploying now"
          value={deployingRecords.length}
          hue={deployingRecords.length > 0 ? '#0b76d1' : undefined}
          to="/activity"
          icon={<DeployIcon sx={{ fontSize: 15, color: '#0b76d1' }} />}
        />
        <StatCard
          label="Needs attention"
          value={attentionRecords.length}
          hue={attentionRecords.length > 0 ? '#d93a3a' : undefined}
          to="/services"
          icon={<FailIcon sx={{ fontSize: 15, color: '#d93a3a' }} />}
        />
        <StatCard
          label="Healthy apps"
          value={healthyCount}
          to="/services"
          icon={<HealthyIcon sx={{ fontSize: 15, color: '#1aae39' }} />}
        />
        <StatCard label="Environments" value={uniqueEnvs.length} to="/environments" />
      </Box>

      {/* Deploying now */}
      {deployingRecords.length > 0 && (
        <SectionCard title="Deploying now" count={deployingRecords.length} hue="#0b76d1">
          {deployingRecords.map((r, i) => (
            <DeploymentRow key={deploymentKey(r, i)} record={r} progress index={i} />
          ))}
        </SectionCard>
      )}

      {/* Needs attention */}
      <SectionCard
        title="Needs attention"
        count={attentionRecords.length}
        hue={attentionRecords.length > 0 ? '#d93a3a' : undefined}
        info={<InfoPopover entry="needsAttention" size={15} />}
      >
        {attentionRecords.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              No unresolved failures. Nice.
            </Typography>
          </Box>
        ) : (
          <>
            {shownAttentionRecords.map((r, i) => (
              <DeploymentRow key={deploymentKey(r, i)} record={r} index={i} />
            ))}
            {attentionRecords.length > MAX_FAILING_SHOWN && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Showing {MAX_FAILING_SHOWN} of {attentionRecords.length}
                </Typography>
              </Box>
            )}
          </>
        )}
      </SectionCard>

      {/* Recent activity */}
      <SectionCard
        title="Recent activity"
        action={
          <Button component={RouterLink} to="/activity" size="small" sx={{ fontSize: '0.78rem' }}>
            View all
          </Button>
        }
      >
        {recentRecords.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <EmptyState
              title="No deployments yet"
              body="Deployments will appear here as services report in."
            />
          </Box>
        ) : (
          recentRecords.map((r, i) => <DeploymentRow key={deploymentKey(r, i)} record={r} index={i} />)
        )}
      </SectionCard>

      <Typography variant="caption" sx={{ opacity: 0.5 }}>
        {staticRecords.length} current records ·{' '}
        <MuiLink component={RouterLink} to="/environments" underline="hover">
          dynamic environments
        </MuiLink>{' '}
        are tracked separately
      </Typography>
    </Box>
  );
};

export default OverviewPage;
